import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Input,
    Button,
    Textarea,
    Typography,
    Alert
} from '@material-tailwind/react';
import Select from 'react-select';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';
import { usePayers } from '../../../../context/PayersContext';

export default function ChargesDialog({
    open,
    charge = null,
    jobCardId,
    chargeTypes = [],
    locked = false,
    onSave,
    onClose
}) {
    const { user } = useContext(AuthContext);
    const { payers, loading: payersLoading } = usePayers();
    const [formData, setFormData] = useState({
        charge_type_id: '',
        description: '',
        amount: 0,
        discount_amount: 0,
        paid_by: '',
        requires_reimbursement: true,
        notes: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (charge) {
            setFormData({
                charge_type_id: charge.charge_type_id,
                description: charge.description || '',
                amount: charge.amount || 0,
                discount_amount: charge.discount_amount || 0,
                paid_by: charge.paid_by || '',
                requires_reimbursement: charge.requires_reimbursement || false,
                notes: charge.notes || ''
            });
        } else {
            setFormData({
                charge_type_id: '',
                description: '',
                amount: 0,
                discount_amount: 0,
                paid_by: '',
                requires_reimbursement: true,
                notes: ''
            });
        }
        setErrors({});
        setError(null);
    }, [charge, open]);

    const calculateTotal = () => {
        const amount = parseFloat(formData.amount || 0);
        const discount = parseFloat(formData.discount_amount || 0);
        return Math.max(0, amount - discount);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.charge_type_id) {
            newErrors.charge_type_id = 'Charge type is required';
        }

        const amount = parseFloat(formData.amount);
        if (!amount || amount <= 0) {
            newErrors.amount = 'Amount must be greater than zero';
        }

        const discount = parseFloat(formData.discount_amount || 0);
        if (discount < 0) {
            newErrors.discount_amount = 'Discount cannot be negative';
        }
        if (discount > amount) {
            newErrors.discount_amount = 'Discount cannot exceed amount';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            setError(null);

            const url = charge
                ? `${rootUrl}/api/job-cards/${jobCardId}/charges/${charge.id}`
                : `${rootUrl}/api/job-cards/${jobCardId}/charges`;

            const method = charge ? 'put' : 'post';

            const response = await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });

            if (response.data.success) {
                onSave();
                onClose();
            } else {
                setError(response.data.message || 'Failed to save charge');
            }
        } catch (err) {
            console.error('Error saving charge:', err);
            setError(err.response?.data?.message || 'Failed to save charge');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} handler={onClose} size="sm">
            <DialogHeader className="flex justify-between items-center">
                <span>{charge ? 'Edit Charge' : 'Add Charge'}</span>
                {locked && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Locked</span>}
            </DialogHeader>

            <DialogBody divider>
                {error && <Alert color="red" className="mb-4">{error}</Alert>}

                {locked && (
                    <Alert color="yellow" className="mb-4">
                        This charge is locked and cannot be edited.
                    </Alert>
                )}

                <div className="space-y-4">
                    {/* Charge Type & Amount - Two columns on desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Charge Type */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Charge Type *
                            </label>
                            <Select
                                value={chargeTypes.find(ct => ct.id === parseInt(formData.charge_type_id))}
                                onChange={(selectedOption) => handleChange('charge_type_id', selectedOption?.id || '')}
                                options={chargeTypes}
                                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                                getOptionValue={(option) => option.id.toString()}
                                isDisabled={locked}
                                isClearable
                                placeholder="Select charge type"
                                className={errors.charge_type_id ? 'border-red-500' : ''}
                            />
                            {errors.charge_type_id && (
                                <Typography className="text-xs text-red-500 mt-1">{errors.charge_type_id}</Typography>
                            )}
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Amount *
                            </label>
                            <Input
                                type="number"
                                value={formData.amount}
                                onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                                disabled={locked}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                error={!!errors.amount}
                                className="text-right"
                            />
                            {errors.amount && (
                                <Typography className="text-xs text-red-500 mt-1">{errors.amount}</Typography>
                            )}
                        </div>
                    </div>

                    {/* Discount & Paid By - Two columns on desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Discount */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Discount
                            </label>
                            <Input
                                type="number"
                                value={formData.discount_amount}
                                onChange={(e) => handleChange('discount_amount', parseFloat(e.target.value) || 0)}
                                disabled={locked}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                error={!!errors.discount_amount}
                                className="text-right"
                            />
                            {errors.discount_amount && (
                                <Typography className="text-xs text-red-500 mt-1">{errors.discount_amount}</Typography>
                            )}
                        </div>

                        {/* Paid By */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Paid By
                            </label>
                            <Select
                                value={formData.paid_by ? { value: formData.paid_by, label: payers.find(p => p.code === formData.paid_by)?.name || formData.paid_by } : null}
                                onChange={(selectedOption) => handleChange('paid_by', selectedOption?.value || '')}
                                options={payers.map(p => ({ value: p.code, label: p.name }))}
                                isDisabled={locked || payersLoading}
                                isClearable
                                placeholder={payersLoading ? "Loading payers..." : "Select who pays"}
                            />
                        </div>
                    </div>

                    {/* Net Total */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-700">Net Total:</span>
                            <span className="text-lg font-bold text-blue-600">
                                {new Intl.NumberFormat('en-KE', {
                                    style: 'currency',
                                    currency: 'KES'
                                }).format(calculateTotal())}
                            </span>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Description
                        </label>
                        <Textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            disabled={locked}
                            placeholder="Enter charge description"
                            rows={2}
                        />
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            disabled={locked}
                            placeholder="Additional notes"
                            rows={2}
                        />
                    </div>
                </div>
            </DialogBody>

            <DialogFooter>
                <Button
                    variant="text"
                    color="gray"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                {!locked && (
                    <Button
                        color="blue"
                        onClick={handleSave}
                        loading={loading}
                        disabled={loading}
                    >
                        {charge ? 'Update' : 'Add'} Charge
                    </Button>
                )}
            </DialogFooter>
        </Dialog>
    );
}
